<template>
    <form-modal
        :title="updateModalTitle"
        v-model="model"
        submit-button="Update"
        @submit="dismiss"
        :modal-style="'max-width:950px'">
        <div class="error-warning-update">
            <div><span class="h4">Agency Name:</span> {{ dataItem.agency }}</div>
            <div><span class="h4">Agency Ori:</span> {{ dataItem.ori }}</div>
            <div>
                <span class="h4">{{ incidentNumberLabel }}:</span> {{ idValue }}
            </div>
            <div>
                <span class="h4">Description:</span>
                <div class="line">
                    <span>{{ typeAndCode }}</span> - {{ errorDesc }}
                </div>
            </div>
            <div>
                <span class="h4">Status:</span>
                <div class="line">
                    <div class="d-inline-block">
                        <div
                            class="form-check"
                            v-for="status in statusList">
                            <input
                                class="form-check-input"
                                type="radio"
                                :name="status.id"
                                :id="status.id"
                                :value="status.value"
                                v-model="item.status" />
                            <label
                                class="form-check-label"
                                :for="status.id">
                                {{ status.text }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="item.status == 1">
                <label
                    for="explanation"
                    class="h4"
                    >Explanation/Justification:</label
                >
                <div
                    class="line"
                    style="width: 80%">
                    <b-textarea
                        name="explanation"
                        v-model="item.explanation"
                        :error="errors.explanation"
                        :rows="5">
                    </b-textarea>
                </div>                
            </div>

            <template v-if="dataItem.isAcknowledged && item.status == 1">
                <div class="mt-3"><span class="h4">Action date:</span> {{ dataItem.acknowledgedOn }}</div>
                <div><span class="h4">Actioned by:</span> {{ dataItem.acknowledgedBy }}</div>
            </template>
        </div>
    </form-modal>
</template>
<script setup lang="ts">
import {computed, ref, watch, defineProps, defineEmits, onMounted} from "vue";
import {useGlobalMessagesStore} from "@/stores/globalMessagesStore";
import axios from "axios";
import {handleError} from "@/stores/storeHelper";
import {DismissError} from "@/types";
import {number, object, string} from "yup";
import {BTextarea} from "./Fields";
import {FormModal} from "./Misc";
import {useFormValidation} from "@/validation";
const {handleSubmit, errors, resetValidation} = useFormValidation();
const model = defineModel<boolean>();
const item = ref({} as DismissError);
const schema = object<DismissError>({
    status: number().required().label("Status"),
    explanation: string().nullable()
        .label("Explanation/Justification")
        .when([], {
            is: () => item.value.status == 1,
            then: (schema) => schema.required(),
        }),
});

const globalMessages = useGlobalMessagesStore();

const props = withDefaults(
    defineProps<{
        item: any;
        url: string;
        dataset: string;
        uploadHeaders?: any;
    }>(),
    {
        uploadHeaders: {},
    }
);

const emit = defineEmits<{
    (e: "dismiss"): void;
}>();

var dataItem = ref({} as any);

const statusList = computed(() => {
    return [
        {id: "unresolved", text: "Unresolved", value: "0"},
        {id: "dismissed", text: props.item.errorType == "Warning" ? "Cleared" : "Dismissed", value: "1"},
    ];
});

const updateModalTitle = computed(() => {
    if (dataItem.value.errorType == "Warning") {
        return "Warning Status Update";
    } else {
        return "Error Status Update";
    }
});

const incidentNumberLabel = computed(() => {
    if (props.dataset?.toLocaleLowerCase() == "communityPolicing") {
        return "Stop Number";
    }
    return "Incident Number";
});
const typeAndCode = computed(() => {
    return `${dataItem.value["errorType"]} ${dataItem.value["errorCode"]}`;
});
const errorDesc = computed(() => {
    return dataItem.value["description"];
});
const idValue = computed(() => {
    return props.dataset == "communityPolicing" ? dataItem.value["stopNumber"] : dataItem.value["incidentNumber"];
});

const dismiss = async () => {
    let dataToPost = {
        id: dataItem.value.id,
        status: item.value.status,
        explanation: item.value.explanation,
        code: dataItem.value.errorCode,
    };

    if (await handleSubmit(schema, item.value)) {
        try {
            await axios.post(props.url, dataToPost, {
                headers: props.uploadHeaders,
            });
            globalMessages.push({message: "Error or warning updated successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            handleDismissClick();
        }
    }
};
function handleDismissClick() {
    model.value = false;
    emit("dismiss");
}

watch(
    () => props.item,
    (newItem, prevItem) => {
        resetValidation();
        if (newItem != null) {
            dataItem.value = newItem;

            if (dataItem.value["isAcknowledged"]) {
                item.value.status = 1;
            } else {
                item.value.status = 0;
            }

            item.value.explanation = dataItem.value["explanation"];
        } else {
            item.value = {} as DismissError;
        }
    }
);
</script>
<style lang="scss">
.error-warning-update {
    .h4 {
        display: inline-block;
        width: 140px;
    }
    .line {
        margin-left: 30px;
    }
}
</style>
